/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'

import type { WithEuiThemeProps } from '@elastic/eui'
import {
  EuiButtonEmpty,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiOverlayMask,
  withEuiTheme,
} from '@elastic/eui'

import type {
  ElasticsearchClusterPlan,
  DeploymentSearchResponse,
} from '@modules/cloud-api/v1/types'
import type { AsyncRequestState, StackDeployment } from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'

import { getRegionId } from '@/lib/stackDeployments/selectors/fundamentals'

import SelectSnapshotSource from '../../../StackDeploymentEditor/CreateStackDeploymentEditor/SelectTemplate/SetupDeployment/SetupSnapshots/SelectSnapshotSource'

import type { ConsumerProps as SelectSnapshotSourceProps } from '../../../StackDeploymentEditor/CreateStackDeploymentEditor/SelectTemplate/SetupDeployment/SetupSnapshots/SelectSnapshotSource'

interface Props {
  plan: ElasticsearchClusterPlan
  deploymentUnderEdit: StackDeployment
  onChangeSnapshotSource: (value?: DeploymentSearchResponse | null) => void
  onSelectSnapshot: SelectSnapshotSourceProps['onSelectSnapshot']
  onUpdateIndexRestore: SelectSnapshotSourceProps['onUpdateIndexRestore']
  onRestoreSnapshot: SelectSnapshotSourceProps['onRestoreSnapshot']
  updateDeploymentRequest: AsyncRequestState
  onClose: () => void
  searchDeployments: (userInput: string) => void
  onChangeMaintMode?: (value) => void
}

class SnapshotRestoreModal extends Component<Props & WithEuiThemeProps> {
  render(): JSX.Element {
    const { onClose, theme } = this.props

    return (
      <EuiOverlayMask>
        <EuiModal onClose={() => onClose()} style={{ width: theme.euiTheme.breakpoint.m }}>
          {this.renderHeader()}
          {this.renderBody()}
          {this.renderFooter()}
        </EuiModal>
      </EuiOverlayMask>
    )
  }

  renderHeader(): JSX.Element {
    return (
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <FormattedMessage id='snapshot-restore.title' defaultMessage='Restore snapshot' />
        </EuiModalHeaderTitle>
      </EuiModalHeader>
    )
  }

  renderBody(): JSX.Element | null {
    const {
      plan,
      deploymentUnderEdit,
      updateDeploymentRequest,
      searchDeployments,
      onChangeMaintMode,
    } = this.props

    const version = plan.elasticsearch.version

    if (!version) {
      return null
    }

    const restoreSnapshotConfiguration = plan.transient?.restore_snapshot
    const snapshotRestoreSettings = restoreSnapshotConfiguration
      ? {
          snapshot_name: restoreSnapshotConfiguration.snapshot_name!,
          source_cluster_id: restoreSnapshotConfiguration.source_cluster_id!,
        }
      : undefined

    const regionId = getRegionId({ deployment: deploymentUnderEdit })
    const extendedMaintenance = this.props.plan.transient?.plan_configuration?.extended_maintenance
    const extendedMaintenanceToSend = extendedMaintenance == null ? true : extendedMaintenance

    return (
      <EuiModalBody>
        <SelectSnapshotSource
          searchDeployments={searchDeployments}
          regionId={regionId}
          version={version}
          onSelectSnapshot={this.props.onSelectSnapshot}
          onRestoreSnapshot={this.props.onRestoreSnapshot}
          onUpdateIndexRestore={this.props.onUpdateIndexRestore}
          restoreSnapshotRequest={updateDeploymentRequest}
          forceLastSnapshot={false}
          showRegion={false}
          snapshotRestoreSettings={snapshotRestoreSettings}
          onUpdateSnapshotSource={this.props.onChangeSnapshotSource}
          asRestoreForm={true}
          onChangeMaintMode={(value) => onChangeMaintMode && onChangeMaintMode(value)}
          maintModeValue={extendedMaintenanceToSend}
        />

        {updateDeploymentRequest.error && (
          <CuiAlert type='error'>
            <FormattedMessage
              id='cluster-snapshots.latest-snapshot-error'
              defaultMessage='Something went wrong with trying to take a snapshot.'
            />
          </CuiAlert>
        )}
      </EuiModalBody>
    )
  }

  renderFooter(): JSX.Element {
    const { onClose } = this.props

    return (
      <EuiModalFooter>
        <EuiButtonEmpty onClick={() => onClose()}>
          <FormattedMessage id='snapshot-restore.close' defaultMessage='Close' />
        </EuiButtonEmpty>
      </EuiModalFooter>
    )
  }
}

export default withEuiTheme(SnapshotRestoreModal)
